import { deletePosOrder, getPosOrders, updatePosOrder } from '../modules/posRestaurant/api';
import {
  formatRestaurantOrderNumber,
  getOrderItemIdentityKey,
  normalizeQueueTimers,
  normalizeVariationId,
  snapToIncomingPrepOption,
} from './restaurantQrIncomingOrders';

function normalizeDraftOrderItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((row) => ({
      menu_item: Number(row?.menu_item),
      quantity: Math.max(1, Math.floor(Number(row?.quantity || 1))),
      note: String(row?.special_note || row?.note || '').trim(),
      variation_id: normalizeVariationId(row?.variation_id),
      variation_label: String(row?.variation_name || row?.variation_label || '').trim(),
      variation_price: Math.max(0, Number(row?.variation_price || 0)),
      unit_price: row?.unit_price,
    }))
    .filter((row) => Number(row.menu_item) > 0);
}

function mergeDraftOrderItems(baseItems = [], incomingItems = []) {
  const mergedItems = [];
  const itemIndexMap = new Map();

  [...baseItems, ...incomingItems].forEach((row) => {
    const menuItemId = Number(row?.menu_item);
    const quantity = Math.max(1, Math.floor(Number(row?.quantity || 1)));
    const note = String(row?.note || '').trim();
    const rowIdentityKey = getOrderItemIdentityKey(row);
    if (!menuItemId) return;

    if (!itemIndexMap.has(rowIdentityKey)) {
      itemIndexMap.set(rowIdentityKey, mergedItems.length);
      mergedItems.push({
        menu_item: menuItemId,
        quantity,
        note,
        variation_id: normalizeVariationId(row?.variation_id),
        variation_label: String(row?.variation_label || row?.variation_name || '').trim(),
        variation_price: Math.max(0, Number(row?.variation_price || 0)),
        unit_price: row?.unit_price,
      });
      return;
    }

    const existingIdx = itemIndexMap.get(rowIdentityKey);
    const existingRow = mergedItems[existingIdx];
    mergedItems[existingIdx] = {
      ...existingRow,
      quantity: Number(existingRow.quantity || 0) + quantity,
      note: [existingRow.note, note].filter(Boolean).join(existingRow.note && note ? ' | ' : ''),
    };
  });

  return mergedItems;
}

function mergeQueueTimersForMergedOrder(destinationTimers = [], sourceTimers = []) {
  const normalizedDestinationTimers = normalizeQueueTimers(destinationTimers);
  const normalizedSourceTimers = normalizeQueueTimers(sourceTimers);
  const destinationActive = normalizedDestinationTimers.find((timer) => !timer.ended_at) || null;
  const sourceActive = normalizedSourceTimers.find((timer) => !timer.ended_at) || null;

  const mergedTimers = [
    ...normalizedDestinationTimers.filter((timer) => timer.ended_at),
    ...normalizedSourceTimers.filter((timer) => timer.ended_at),
  ];

  if (destinationActive || sourceActive) {
    const mergedActiveTargetMinutes = Number(destinationActive?.target_minutes || 0)
      + Number(sourceActive?.target_minutes || 0);
    const mergedActiveStartCandidates = [destinationActive?.started_at, sourceActive?.started_at]
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    const mergedActiveStartedAt = mergedActiveStartCandidates.length
      ? new Date(Math.min(...mergedActiveStartCandidates)).toISOString()
      : (destinationActive?.started_at || sourceActive?.started_at || new Date().toISOString());

    mergedTimers.push({
      timer_id: 'timer-active',
      started_at: mergedActiveStartedAt,
      ended_at: null,
      target_minutes: Math.max(10, mergedActiveTargetMinutes || 30),
    });
  }

  return mergedTimers.map((timer, index) => ({
    ...timer,
    timer_id: `timer-${index + 1}`,
  }));
}

function getEditableOrderNote(rawNote) {
  const note = String(rawNote || '');
  const profileIndex = note.indexOf(' | Mobile:');
  if (profileIndex > -1) return note.slice(0, profileIndex).trim();
  return note;
}

function buildIncomingOrderDecisionPayload(order, nextStatus, nextPreparationMinutes) {
  const normalizedPreparationMinutes = snapToIncomingPrepOption(
    nextPreparationMinutes || order?.preparation_time_minutes || 30,
  );
  const sourceItems = Array.isArray(order?.items) ? order.items : [];
  const orderItems = sourceItems
    .filter((item) => Number(item?.menu_item || 0) > 0 && Number(item?.quantity || 0) > 0)
    .map((item) => ({
      menu_item: Number(item.menu_item),
      variation_id: normalizeVariationId(item?.variation_id),
      quantity: Math.max(1, Number(item.quantity || 1)),
      special_note: String(item?.special_note || '').trim(),
    }));
  const existingQueueTimers = normalizeQueueTimers(order?.queue_timers);
  const nowIso = new Date().toISOString();
  const nextQueueTimers = nextStatus === 'preparing'
    ? [
      ...existingQueueTimers.map((timer) => (timer?.ended_at ? timer : { ...timer, ended_at: nowIso })),
      {
        timer_id: `timer-${existingQueueTimers.length + 1}`,
        started_at: nowIso,
        ended_at: null,
        target_minutes: normalizedPreparationMinutes,
      },
    ]
    : existingQueueTimers.map((timer) => (timer?.ended_at ? timer : { ...timer, ended_at: nowIso }));

  return {
    status: nextStatus,
    preparation_time_minutes: normalizedPreparationMinutes,
    queue_timers: nextQueueTimers,
    order_items: orderItems,
    audit_context: {
      action: nextStatus === 'preparing' ? 'kot_created' : 'order_cancelled',
      label: nextStatus === 'preparing' ? 'Order Accepted' : 'Order Declined',
      message: nextStatus === 'preparing'
        ? `Order ${formatRestaurantOrderNumber(order?.order_number)} accepted in restaurant queue.`
        : `Order ${formatRestaurantOrderNumber(order?.order_number)} declined in restaurant queue.`,
      details: {
        source: 'global_qr_order_popup',
        order_id: Number(order?.id || 0) || null,
        order_number: formatRestaurantOrderNumber(order?.order_number),
        preparation_time_minutes: normalizedPreparationMinutes,
      },
    },
  };
}

function findRunningMergeTargetOrder(orders, incomingOrder) {
  const sessionId = Number(incomingOrder?.qr_table_session || 0);
  const roomId = Number(incomingOrder?.room || 0);
  const isRoomServiceOrder = String(incomingOrder?.order_type || '').toLowerCase() === 'room_service';
  const incomingOrderId = Number(incomingOrder?.id || 0);
  return (orders || [])
    .filter((order) => {
      if (Number(order?.id || 0) === incomingOrderId) return false;
      if (sessionId) {
        if (Number(order?.qr_table_session || 0) !== sessionId) return false;
      } else if (isRoomServiceOrder && roomId) {
        if (Number(order?.room || 0) !== roomId) return false;
        if (String(order?.order_type || '').toLowerCase() !== 'room_service') return false;
      } else {
        return false;
      }
      const status = String(order?.status || '').toLowerCase();
      return status === 'preparing' || status === 'placed' || status === 'served';
    })
    .sort((first, second) => new Date(second?.updated_at || second?.created_at || 0).getTime()
      - new Date(first?.updated_at || first?.created_at || 0).getTime())[0] || null;
}

export async function acceptIncomingQrOrder(order, {
  orders = [],
  preparationMinutes = null,
} = {}) {
  const orderId = Number(order?.id || 0);
  if (!orderId) throw new Error('Invalid order.');

  const resolvedPreparationMinutes = snapToIncomingPrepOption(
    preparationMinutes || order?.preparation_time_minutes || 30,
  );
  const payload = buildIncomingOrderDecisionPayload(order, 'preparing', resolvedPreparationMinutes);
  const runningSessionOrder = findRunningMergeTargetOrder(orders, order);

  if (runningSessionOrder) {
    const isRoomServiceMerge = String(order?.order_type || '').toLowerCase() === 'room_service' && Number(order?.room || 0);
    const mergeSource = isRoomServiceMerge ? 'room_service_accept' : 'qr_table_session_accept';
    const mergedItems = mergeDraftOrderItems(
      normalizeDraftOrderItems(runningSessionOrder?.items || []),
      normalizeDraftOrderItems(order?.items || []),
    );
    const mergedQueueTimers = mergeQueueTimersForMergedOrder(
      runningSessionOrder?.queue_timers || [],
      payload.queue_timers || [],
    );
    const destinationNote = getEditableOrderNote(runningSessionOrder?.notes);
    const incomingNote = getEditableOrderNote(order?.notes);
    const mergedNote = [destinationNote, incomingNote].filter(Boolean).join(destinationNote && incomingNote ? '\n' : '');

    const mergedPayload = {
      order_type: runningSessionOrder.order_type || order.order_type || 'dine_in',
      status: 'preparing',
      room: runningSessionOrder.room || null,
      table: runningSessionOrder.table || order.table || null,
      customer_name: runningSessionOrder.customer_name || order.customer_name || '',
      customer_phone: runningSessionOrder.customer_phone || order.customer_phone || '',
      notes: mergedNote,
      preparation_time_minutes: resolvedPreparationMinutes,
      queue_timers: mergedQueueTimers,
      order_items: mergedItems.map((item) => ({
        menu_item: Number(item.menu_item),
        variation_id: normalizeVariationId(item.variation_id),
        quantity: Math.max(1, Number(item.quantity || 1)),
        special_note: String(item.note || '').trim(),
      })),
      audit_context: {
        action: 'order_merged',
        label: isRoomServiceMerge ? 'Room Service Order Merged' : 'QR Table Order Merged',
        message: `Order ${formatRestaurantOrderNumber(order?.order_number)} merged into ${formatRestaurantOrderNumber(runningSessionOrder?.order_number)}.`,
        details: {
          source: mergeSource,
          merged_into_order_id: Number(runningSessionOrder?.id || 0),
        },
      },
    };

    const updatedOrder = await updatePosOrder(runningSessionOrder.id, mergedPayload);
    try {
      await deletePosOrder(orderId);
    } catch {
      await updatePosOrder(orderId, {
        status: 'cancelled',
        queue_timers: normalizeQueueTimers(order?.queue_timers || [])
          .map((timer) => (timer.ended_at ? timer : { ...timer, ended_at: new Date().toISOString() })),
        tax_breakdown: [],
        order_items: [],
        audit_context: {
          action: 'merge_source_closed',
          label: isRoomServiceMerge ? 'Merged Room Service Source Closed' : 'Merged QR Source Closed',
          message: `Order ${formatRestaurantOrderNumber(order?.order_number)} was merged into ${formatRestaurantOrderNumber(runningSessionOrder?.order_number)}.`,
          details: {
            source: mergeSource,
            merged_into_order_id: Number(runningSessionOrder?.id || 0),
          },
        },
      });
    }
    return { action: 'merged', orderId, targetOrder: updatedOrder };
  }

  await updatePosOrder(orderId, payload);
  return { action: 'accepted', orderId };
}

export async function declineIncomingQrOrder(order, { preparationMinutes = null } = {}) {
  const orderId = Number(order?.id || 0);
  if (!orderId) throw new Error('Invalid order.');
  const resolvedPreparationMinutes = snapToIncomingPrepOption(
    preparationMinutes || order?.preparation_time_minutes || 30,
  );
  const payload = buildIncomingOrderDecisionPayload(order, 'cancelled', resolvedPreparationMinutes);
  await updatePosOrder(orderId, payload);
  return { action: 'declined', orderId };
}

export async function fetchRestaurantOrdersList() {
  const ordersPayload = await getPosOrders();
  if (Array.isArray(ordersPayload)) return ordersPayload;
  if (Array.isArray(ordersPayload?.results)) return ordersPayload.results;
  return [];
}
